// src/pages/AttendancePage.js
//
// CORRECTIONS v4 :
//  • Navigation dates : flèches ← → en plus du bouton "Changer de date".
//  • Source : 'mobile_app', 'agenda_auto', 'agenda_attendance', 'agenda_card10'
//    → tous mappés à "App 📱".
//  • consumedFromCard=true → badge "Carte 10" orangé dans la colonne Formule.
//  • Déduplication renforcée : on fusionne par studentId pour éviter
//    les doublons (même personne, 3 registrations différentes).
//  • Données manquantes dans manual_attendance → affichage gracieux
//    (email de l'utilisateur connecté si disponible dans le record).
//
// v5 : VUE "PAR DATE" — bascule "Par cours" / "Par date" en haut de page.
//  • "Par date" : on choisit un jour (flèches ou sélecteur), la page liste
//    TOUS les cours ayant une séance ce jour-là, avec le nombre d'attendus /
//    présents / absents et un statut (pas saisi / partiel / complet).
//  • Cliquer sur un cours de cette liste ouvre directement la vue "Par cours"
//    habituelle, sur ce cours et cette date précise (au lieu de la prochaine
//    séance par défaut).
//
// v6 : LIEN PRÉSENCES ↔ INSCRIPTIONS ↔ PAIEMENTS (src/utils/billing.js)
//  • Dédoublonnage par IDENTITÉ (studentId OU prénom + nom normalisés) : un
//    élève inscrit sur le site ET avec une carte 10 dans l'app n'apparaît plus
//    qu'une fois. Les frères et sœurs d'un même compte parent ne sont plus
//    fusionnés (l'ancien repli sur appUserId cachait le 2e enfant).
//  • Quand un élève a plusieurs inscriptions, la plus pertinente est retenue
//    (abonnement > carte 10 avec crédit > cours unique > essai).
//  • Badge « paiement » à côté de la formule : retard, échec de prélèvement,
//    carte épuisée / crédits restants, non payé.
//  • Carte 10 : section « titulaires d'une carte 10 sans réservation » — un
//    clic marque présent ET décompte le crédit. À l'enregistrement, un
//    titulaire de carte présent non décompté est décompté ; un absent dont le
//    crédit avait été décompté par l'admin est recrédité (jamais ce que l'app
//    a décompté lors d'une réservation).
//  • Les inscriptions annulées (status 'cancelled') ne sont plus attendues.

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { db } from '../firebase';
import {
  collection, getDocs, doc, getDoc, setDoc, deleteDoc,
  Timestamp, addDoc,
} from 'firebase/firestore';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Checkbox, CircularProgress, Button, Grid,
  Card, CardContent, Divider, Chip, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, MenuItem, FormControl, InputLabel, Select,
  FormControlLabel, Switch, AppBar, Toolbar, IconButton, Tooltip, Badge,
  ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  Event as EventIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  CalendarToday,
  PersonAdd as PersonAddIcon,
  ArrowBack as ArrowBackIcon,
  Delete as DeleteIcon,
  PhoneAndroid as AppIcon,
  ChevronLeft as PrevIcon,
  ChevronRight as NextIcon,
  CreditCard as CardIcon,
  Videocam as ImageYesIcon,
  VideocamOff as ImageNoIcon,
  HelpOutline as ImageUnknownIcon,
} from '@mui/icons-material';
import moment from 'moment';
import 'moment/locale/fr';
import {
  personIdentityKeys, registrationPersons, registrationPriority, registrationKind,
  attendanceBadge, cardCredits, consumptionMatches, BADGE_TONES, readDate,
} from '../utils/billing';
import { consumeCardCredit, removeCardConsumption } from '../utils/billingActions';
import { groupPeople, recordBirth, findSamePerson } from '../utils/studentIdentity';
moment.locale('fr');

// Sources de consommation de carte 10 créées depuis l'admin (recréditables
// si l'élève est finalement noté absent) — jamais les consommations de l'app.
const ADMIN_CARD_SOURCES = ['admin_attendance'];

// ──────────────────────────────────────────────────────────────────────────────
// Helpers
// ──────────────────────────────────────────────────────────────────────────────

const toDateKey = (ds) => moment(ds).format('YYYY-MM-DD');

/**
 * Clé du document record (courses/{c}/attendance/{date}/records/{clé}) pour
 * une personne d'une inscription. Même logique qu'avant (studentId puis id
 * d'attendee), SAUF appUserId/targetStudentId qui ne servent que pour une
 * inscription à une seule personne : sinon deux frères et sœurs écrivaient
 * dans le même document.
 */
const recordKeyFor = (reg, person, idx) => {
  const single = registrationPersons(reg).length <= 1;
  return person?.studentId
    || (single ? (reg.targetStudentId || reg.appUserId) : null)
    || `${reg.id}_${idx}`;
};

/** Icône compacte "droit à l'image" à afficher à côté du nom d'un participant */
const ImageConsentIcon = ({ consent }) => {
  if (consent === true) {
    return (
      <Tooltip title="Autorise l'utilisation de l'image">
        <ImageYesIcon fontSize="small" sx={{ color: '#2e7d32' }} />
      </Tooltip>
    );
  }
  if (consent === false) {
    return (
      <Tooltip title="N'autorise PAS l'utilisation de l'image">
        <ImageNoIcon fontSize="small" sx={{ color: '#c62828' }} />
      </Tooltip>
    );
  }
  return (
    <Tooltip title="Droit à l'image non renseigné">
      <ImageUnknownIcon fontSize="small" sx={{ color: '#9e9e9e' }} />
    </Tooltip>
  );
};

/** Retourne true si une inscription concerne ce cours */
const matchesCourse = (reg, courseId) =>
  (reg.courseId ?? '') === courseId ||
  (Array.isArray(reg.selectedCourseIds) && reg.selectedCourseIds.includes(courseId));

/**
 * Droit à l'image : source de vérité = persons[].imageConsent (par personne).
 * Fallback sur les anciens champs (imageRightsConsentAll / imageRightsConsent)
 * pour les inscriptions créées avant le passage au consentement par enfant.
 * Retourne true / false / null (non renseigné).
 */
const getPersonImageConsent = (person, reg) => {
  if (person && person.imageConsent !== undefined && person.imageConsent !== null) {
    return person.imageConsent;
  }
  if (reg?.imageRightsConsentAll !== undefined) return reg.imageRightsConsentAll;
  if (reg?.imageRightsConsent !== undefined) return reg.imageRightsConsent;
  return null;
};

/** Retourne true si une inscription couvre cette date (et ce cours, pour une carte 10) */
const matchesDate = (reg, formattedDate, courseId) => {
  if (reg.selectedDate === formattedDate && !reg.allDates) return true;
  if (reg.allDates) return true;
  if (Array.isArray(reg.selectedDates) && reg.selectedDates.includes(formattedDate)) return true;
  if (reg.plan === 'carte10' && Array.isArray(reg.consumedLessons)) {
    return reg.consumedLessons.some((cl) => consumptionMatches(cl, formattedDate, courseId));
  }
  return false;
};

/** Sources venant de l'app Flutter */
const APP_SOURCES = new Set([
  'mobile_app', 'agenda_auto', 'agenda_attendance', 'agenda_card10',
  'mobile_registration', 'mobile_card10',
]);

const sourceLabel = (source) => {
  if (APP_SOURCES.has(source)) return 'App';
  if (source === 'manual')     return 'Manuel';
  return 'Site';
};

const sourceIcon = (source) => {
  if (APP_SOURCES.has(source)) return <AppIcon fontSize="small" sx={{ color: '#7C3AED' }} />;
  return null;
};

const planColor = (plan) => {
  if (!plan || plan === 'manual' || plan === 'agenda') return 'default';
  if (plan === 'essai')   return 'primary';
  if (plan === 'unique')  return 'secondary';
  if (plan === 'carte10') return 'warning';
  return 'success';
};

// ──────────────────────────────────────────────────────────────────────────────
// Composant principal
// ──────────────────────────────────────────────────────────────────────────────

function AttendancePage() {
  const navigate = useNavigate();

  const [courses,              setCourses]              = useState([]);
  const [selectedCourseId,     setSelectedCourseId]     = useState('');
  const [selectedCourse,       setSelectedCourse]       = useState(null);
  // Liste triée des dates ISO du cours sélectionné
  const [sortedDates,          setSortedDates]          = useState([]);
  const [selectedDateIndex,    setSelectedDateIndex]    = useState(-1);
  const [attendees,            setAttendees]            = useState([]);
  const [attendance,           setAttendance]           = useState({});
  const [loading,              setLoading]              = useState(true);
  const [saving,               setSaving]               = useState(false);
  const [saveSuccess,          setSaveSuccess]          = useState(false);
  const [dateDialogOpen,       setDateDialogOpen]       = useState(false);
  const [dateLoading,          setDateLoading]          = useState(false);
  const [allStudents,          setAllStudents]          = useState([]);
  const [addStudentDialogOpen, setAddStudentDialogOpen] = useState(false);
  const [newStudent,           setNewStudent]           = useState({ firstName: '', lastName: '', birthDate: '', email: '', phone: '', planType: 'manual', addToAllDates: false });
  const [selectedStudentId,    setSelectedStudentId]    = useState('');
  const [deleteDialogOpen,     setDeleteDialogOpen]     = useState(false);
  const [studentToDelete,      setStudentToDelete]      = useState(null);

  // ── Vue "par date" : toutes les séances d'un même jour, tous cours confondus ──
  const [viewMode,             setViewMode]             = useState('course'); // 'course' | 'date'
  const [dateViewDateKey,      setDateViewDateKey]      = useState(() => moment().format('YYYY-MM-DD'));
  const [dateViewCourses,      setDateViewCourses]      = useState([]);
  const [dateViewLoading,      setDateViewLoading]      = useState(false);
  // Date précise à sélectionner quand on arrive sur un cours depuis la vue
  // par date (au lieu de la prochaine séance par défaut) — une ref, pas un
  // state, car elle est lue une seule fois par l'effet de chargement du cours
  // puis aussitôt remise à null : elle n'a pas besoin de déclencher de rendu.
  const pendingDateKeyRef      = useRef(null);

  // Inscriptions chargées (id → données) pour le badge paiement et le
  // décompte carte 10 à l'enregistrement.
  const regsByIdRef            = useRef({});
  // Titulaires d'une carte 10 valable sur ce cours, sans réservation ce jour-là
  const [cardHolders,          setCardHolders]          = useState([]);
  const [cardActionId,         setCardActionId]         = useState(null);
  const [saveWarnings,         setSaveWarnings]         = useState([]);

  // Date sélectionnée (ISO string)
  const selectedDate = selectedDateIndex >= 0 && selectedDateIndex < sortedDates.length
    ? sortedDates[selectedDateIndex]
    : null;

  // ── Chargement des cours ───────────────────────────────────────────────────

  useEffect(() => {
    const fetch = async () => {
      try {
        const snap = await getDocs(collection(db, 'dance_courses'));
        setCourses(snap.docs.map(d => ({ id: d.id, title: d.data().title, dates: d.data().dates || [] })));
      } catch (e) {
        console.error('Erreur cours:', e);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  useEffect(() => {
    const fetch = async () => {
      try {
        // students/ + élèves de users/ qui n'ont pas encore de fiche students
        // (compte app) — sinon l'admin ne les trouvait pas et recréait l'élève.
        const [snap, usersSnap] = await Promise.all([
          getDocs(collection(db, 'students')),
          getDocs(collection(db, 'users')),
        ]);
        const list = snap.docs.map(d => ({ id: d.id, ...d.data(), _src: 'students' }));
        const known = new Set(list.map(s => s.id));
        usersSnap.docs.forEach(d => {
          const data = d.data();
          if (['Élève', 'Élève adulte'].includes(data.role) && !known.has(d.id)) {
            list.push({ id: d.id, ...data, _src: 'users' });
          }
        });
        setAllStudents(list);
      } catch (e) { console.error('Erreur étudiants:', e); }
    };
    fetch();
  }, []);

  // ── Chargement cours sélectionné + initialisation des dates ───────────────

  useEffect(() => {
    if (!selectedCourseId) return;
    const fetch = async () => {
      setLoading(true);
      setAttendees([]);
      setCardHolders([]);
      setAttendance({});
      setSortedDates([]);
      setSelectedDateIndex(-1);
      try {
        const snap = await getDoc(doc(db, 'dance_courses', selectedCourseId));
        if (!snap.exists()) return;
        const data   = snap.data();
        const course = { id: selectedCourseId, ...data };
        setSelectedCourse(course);

        // Trier les dates et trouver la prochaine
        const today  = moment().startOf('day');
        const sorted = [...(data.dates || [])]
          .map(d => ({ iso: moment(d.date ?? d).toISOString(), _m: moment(d.date ?? d) }))
          .sort((a, b) => a._m - b._m);
        const isos = sorted.map(d => d.iso);
        setSortedDates(isos);

        // Une date précise était demandée (clic depuis la vue "Par date") :
        // on la sélectionne au lieu de la prochaine séance par défaut.
        const wantedKey = pendingDateKeyRef.current;
        pendingDateKeyRef.current = null;
        const wantedIdx = wantedKey
          ? sorted.findIndex(d => d._m.format('YYYY-MM-DD') === wantedKey)
          : -1;

        if (wantedIdx >= 0) {
          setSelectedDateIndex(wantedIdx);
        } else {
          // Sélectionner la prochaine séance (ou la dernière)
          const futureIdx = sorted.findIndex(d => d._m.isSameOrAfter(today));
          setSelectedDateIndex(futureIdx >= 0 ? futureIdx : isos.length - 1);
        }
      } catch (e) {
        console.error('Erreur chargement cours:', e);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [selectedCourseId]);

  // ── Navigation entre les dates ─────────────────────────────────────────────

  const goPrev = () => {
    if (selectedDateIndex > 0) setSelectedDateIndex(i => i - 1);
  };
  const goNext = () => {
    if (selectedDateIndex < sortedDates.length - 1) setSelectedDateIndex(i => i + 1);
  };

  const handleDateSelect = (iso) => {
    const idx = sortedDates.indexOf(iso);
    if (idx >= 0) setSelectedDateIndex(idx);
    setDateDialogOpen(false);
  };

  // ── Vue "par date" : navigation et ouverture d'un cours ────────────────────

  const goDateViewPrevDay = () => setDateViewDateKey(k => moment(k).subtract(1, 'day').format('YYYY-MM-DD'));
  const goDateViewNextDay = () => setDateViewDateKey(k => moment(k).add(1, 'day').format('YYYY-MM-DD'));

  const openCourseFromDateView = (courseId, dateKey) => {
    if (courseId === selectedCourseId) {
      // Le cours est déjà chargé en mémoire (sortedDates) : pas besoin de
      // repasser par l'effet de chargement, on sélectionne l'index directement.
      const idx = sortedDates.findIndex(iso => moment(iso).format('YYYY-MM-DD') === dateKey);
      if (idx >= 0) setSelectedDateIndex(idx);
    } else {
      pendingDateKeyRef.current = dateKey;
      setSelectedCourseId(courseId);
    }
    setViewMode('course');
  };

  // ── Chargement participants pour la date sélectionnée ─────────────────────

  const fetchAttendees = useCallback(async () => {
    if (!selectedCourseId || !selectedDate) return;
    setDateLoading(true);
    try {
      const formattedDate = toDateKey(selectedDate);

      // Déduplication par IDENTITÉ : clé d'identité (sid:… / name:…) → id d'attendee
      const keyToAttId = new Map();
      // Map attendeeId → attendee
      const byId       = new Map();

      // Ajoute un candidat, ou le fusionne avec l'attendee qui partage une
      // clé d'identité (on garde alors l'inscription la plus pertinente).
      const addCandidate = (cand, identityKeys) => {
        const keys = identityKeys.length > 0 ? identityKeys : [`id:${cand.id}`];
        const existingId = keys.map(k => keyToAttId.get(k)).find(Boolean);
        if (!existingId) {
          cand.aliasKeys = Array.from(new Set(cand.aliasKeys));
          byId.set(cand.id, cand);
          keys.forEach(k => keyToAttId.set(k, cand.id));
          return;
        }
        const ex = byId.get(existingId);
        ex.aliasKeys = Array.from(new Set([...ex.aliasKeys, ...cand.aliasKeys]));
        if (!ex.studentId && cand.studentId) ex.studentId = cand.studentId;
        if (ex.imageConsent === null && cand.imageConsent !== null) ex.imageConsent = cand.imageConsent;
        if (!ex.contactInfo && cand.contactInfo) ex.contactInfo = cand.contactInfo;
        if ((cand.priority ?? -1) > (ex.priority ?? -1)) {
          ['registrationId', 'plan', 'planName', 'source', 'priority', 'parentInfo'].forEach(f => { ex[f] = cand[f]; });
          if (cand.contactInfo) ex.contactInfo = cand.contactInfo;
        }
        ex.otherPlans = Array.from(new Set([...(ex.otherPlans || []), cand.planName].filter(p => p && p !== ex.planName)));
        keys.forEach(k => keyToAttId.set(k, existingId));
      };

      // ── 1. Registrations ──────────────────────────────────────────────────
      const regsSnap = await getDocs(collection(db, 'registrations'));
      const regsById = {};
      const regsForCourse = [];
      regsSnap.forEach(docSnap => {
        const reg = { id: docSnap.id, ...docSnap.data() };
        regsById[reg.id] = reg;
        if (reg.status === 'cancelled') return;
        if (!matchesCourse(reg, selectedCourseId)) return;
        regsForCourse.push(reg);
      });
      regsByIdRef.current = regsById;

      // Les inscriptions les plus pertinentes d'abord : en cas de doublon,
      // c'est l'abonnement (ou la carte avec crédit) qui « possède » la ligne.
      regsForCourse
        .filter(reg => matchesDate(reg, formattedDate, selectedCourseId))
        .sort((a, b) => registrationPriority(b) - registrationPriority(a))
        .forEach(reg => {
          registrationPersons(reg).forEach((person, idx) => {
            const recordKey = recordKeyFor(reg, person, idx);
            const legacySid = person.studentId || reg.targetStudentId || reg.appUserId || '';
            addCandidate({
              id:              `${reg.id}_${idx}`,
              registrationId:  reg.id,
              studentId:       recordKey === `${reg.id}_${idx}` ? '' : recordKey,
              firstName:       person.firstName || '',
              lastName:        person.lastName  || '',
              birthDate:       person.birthDate,
              plan:            reg.plan,
              planName:        reg.planName || reg.plan || 'N/A',
              contactInfo:     reg.contactInfo,
              parentInfo:      reg.parentInfo,
              source:          reg.source || 'site',
              consumedFromCard:false,
              imageConsent:    getPersonImageConsent(person, reg),
              priority:        registrationPriority(reg),
              // Clés possibles du record déjà enregistré (ancien et nouveau calcul)
              aliasKeys:       [recordKey, `${reg.id}_${idx}`, legacySid].filter(Boolean),
            }, personIdentityKeys(person, reg));
          });
        });

      // ── 2. manual_attendance ──────────────────────────────────────────────
      const manSnap = await getDocs(collection(db, `courses/${selectedCourseId}/manual_attendance`));
      manSnap.forEach(docSnap => {
        const data   = docSnap.data();
        if (data.date !== formattedDate && !data.allDates) return;

        const sid = data.studentId || data.appUserId || '';
        const keys = personIdentityKeys({ firstName: data.firstName, lastName: data.lastName, studentId: sid || undefined }, {});
        // Déjà présent via une inscription (même studentId OU même nom) → pas de doublon
        if (keys.some(k => keyToAttId.has(k))) {
          const ex = byId.get(keys.map(k => keyToAttId.get(k)).find(Boolean));
          if (ex) ex.aliasKeys = Array.from(new Set([...ex.aliasKeys, sid, `manual_${docSnap.id}`].filter(Boolean)));
          return;
        }

        const id = `manual_${docSnap.id}`;
        addCandidate({
          id,
          firstName:         data.firstName || '',
          lastName:          data.lastName  || '',
          birthDate:         data.birthDate,
          plan:              data.plan || 'manual',
          planName:          data.planName || 'Ajout manuel',
          manuallyAdded:     true,
          studentId:         sid,
          addedForAllDates:  data.allDates || false,
          manualAttendanceId:docSnap.id,
          source:            data.source || 'manual',
          contactInfo:       data.contactInfo || null,
          consumedFromCard:  false,
          // Pas d'inscription liée pour un ajout manuel : droit à l'image non renseigné
          // sauf s'il a été explicitement saisi sur le document manual_attendance.
          imageConsent:      data.imageConsent ?? null,
          priority:          -1,
          aliasKeys:         [sid, id].filter(Boolean),
        }, keys);
      });

      const attendeesData = Array.from(byId.values());

      // Badge paiement : situation de l'inscription retenue pour chaque élève
      attendeesData.forEach(att => {
        const reg = att.registrationId ? regsById[att.registrationId] : null;
        if (reg && registrationKind(reg) === 'card'
          && cardCredits(reg).consumed.some(cl => consumptionMatches(cl, formattedDate, selectedCourseId))) {
          att.consumedFromCard = true;
        }
        att.billingBadge = reg
          ? attendanceBadge(reg)
          : (att.plan === 'essai' ? null : { label: 'Aucune formule', tone: 'warning', tooltip: 'Aucune inscription trouvée pour cet élève sur ce cours' });
      });

      // ── 2 bis. Titulaires d'une carte 10 valable, sans réservation ce jour ─
      const takenKeys = new Set(keyToAttId.keys());
      const selectedDay = moment(selectedDate).endOf('day').toDate();
      const holders = [];
      regsForCourse
        .filter(reg => registrationKind(reg) === 'card'
          && cardCredits(reg).remaining > 0
          && (readDate(reg.createdAt) || new Date(0)) <= selectedDay
          && !matchesDate(reg, formattedDate, selectedCourseId))
        .forEach(reg => {
          registrationPersons(reg).forEach((person, idx) => {
            const keys = personIdentityKeys(person, reg);
            if (keys.some(k => takenKeys.has(k))) return;
            keys.forEach(k => takenKeys.add(k));
            holders.push({
              id: `${reg.id}_${idx}`,
              reg,
              person,
              idx,
              credits: cardCredits(reg),
            });
          });
        });
      setCardHolders(holders);

      setAttendees(attendeesData);

      // ── 3. Charger les présences existantes ───────────────────────────────
      const recordsSnap = await getDocs(
        collection(db, `courses/${selectedCourseId}/attendance/${formattedDate}/records`)
      );

      // Les records sont indexés par studentId s'il existe, sinon par l'id
      // de l'attendee — on cherche parmi toutes les clés connues de l'élève
      // (aliasKeys) pour retrouver aussi ceux écrits avant la v6.
      const recordsByKey = {};
      recordsSnap.forEach(r => {
        recordsByKey[r.id] = r.data();
      });

      // Mapper records → attendees
      const newAttState = {};
      attendeesData.forEach(att => {
        const foundKey = [att.studentId, ...att.aliasKeys].filter(Boolean).find(k => recordsByKey[k] !== undefined);
        att.recordKey = foundKey || att.studentId || att.id;
        const rec = foundKey ? recordsByKey[foundKey] : undefined;
        if (rec !== undefined) {
          newAttState[att.id] = rec.present;
          // Enrichir l'attendee avec consumedFromCard si présent dans le record
          if (rec.consumedFromCard) {
            att.consumedFromCard = true;
            att.planName         = rec.planName || 'Carte 10 cours';
          }
        }
      });

      setAttendance(prev => ({ ...prev, [selectedDate]: newAttState }));
      // Forcer la mise à jour des attendees enrichis
      setAttendees([...attendeesData]);
    } catch (e) {
      console.error('Erreur participants:', e);
    } finally {
      setDateLoading(false);
    }
  }, [selectedCourseId, selectedDate]);

  useEffect(() => { fetchAttendees(); }, [fetchAttendees]);

  // Les avertissements d'enregistrement concernent une séance précise
  useEffect(() => { setSaveWarnings([]); }, [selectedCourseId, selectedDate]);

  // ── Vue "par date" : cours ayant une séance ce jour-là, avec un aperçu des
  // présences déjà saisies (tous cours confondus) ───────────────────────────
  useEffect(() => {
    if (viewMode !== 'date') return;
    let cancelled = false;

    const run = async () => {
      setDateViewLoading(true);
      try {
        // Cours ayant une séance ce jour-là (avec horaire, pour tri/affichage)
        const matches = [];
        courses.forEach((c) => {
          (c.dates || []).forEach((d) => {
            if (moment(d.date ?? d).format('YYYY-MM-DD') !== dateViewDateKey) return;
            matches.push({
              courseId:  c.id,
              title:     c.title,
              startTime: d.startTime || null,
              endTime:   d.endTime   || null,
            });
          });
        });
        matches.sort((a, b) => (a.startTime || '').localeCompare(b.startTime || ''));

        if (matches.length === 0) {
          if (!cancelled) setDateViewCourses([]);
          return;
        }

        // Inscriptions chargées UNE SEULE FOIS pour toute la date (plutôt
        // qu'une lecture par cours) — même correspondance que fetchAttendees
        // (matchesCourse + matchesDate + déduplication par studentId), mais
        // sans construire les objets attendee complets : seul le total
        // attendu nous intéresse ici.
        const regsSnap = await getDocs(collection(db, 'registrations'));
        const regs = regsSnap.docs.map(d => ({ id: d.id, ...d.data() }));

        const enriched = await Promise.all(matches.map(async (m) => {
          // Même déduplication par identité que fetchAttendees : on compte des
          // ÉLÈVES, pas des inscriptions.
          const expected = [];   // une entrée par élève = liste de ses clés
          const seen = new Set();
          const addPerson = (keys, fallback) => {
            const ks = keys.length > 0 ? keys : [fallback];
            if (ks.some(k => seen.has(k))) { ks.forEach(k => seen.add(k)); return; }
            ks.forEach(k => seen.add(k));
            expected.push(ks);
          };
          regs.forEach((reg) => {
            if (reg.status === 'cancelled')                         return;
            if (!matchesCourse(reg, m.courseId))                   return;
            if (!matchesDate(reg, dateViewDateKey, m.courseId))    return;
            registrationPersons(reg).forEach((person, idx) => {
              addPerson(personIdentityKeys(person, reg), `id:${reg.id}_${idx}`);
            });
          });

          try {
            const manSnap = await getDocs(collection(db, `courses/${m.courseId}/manual_attendance`));
            manSnap.forEach((docSnap) => {
              const data = docSnap.data();
              if (data.date !== dateViewDateKey && !data.allDates) return;
              const sid = data.studentId || data.appUserId || '';
              addPerson(
                personIdentityKeys({ firstName: data.firstName, lastName: data.lastName, studentId: sid || undefined }, {}),
                `manual_${docSnap.id}`
              );
            });
          } catch (e) {
            console.error('Erreur manual_attendance (vue par date):', e);
          }

          let present = 0;
          let absent  = 0;
          try {
            const recSnap = await getDocs(
              collection(db, `courses/${m.courseId}/attendance/${dateViewDateKey}/records`)
            );
            recSnap.forEach((r) => {
              const p = r.data().present;
              if (p === true)  present += 1;
              if (p === false) absent  += 1;
            });
          } catch (e) {
            console.error('Erreur records (vue par date):', e);
          }

          return { ...m, expectedCount: expected.length, present, absent };
        }));

        if (!cancelled) setDateViewCourses(enriched);
      } catch (e) {
        console.error('Erreur chargement vue par date:', e);
        if (!cancelled) setDateViewCourses([]);
      } finally {
        if (!cancelled) setDateViewLoading(false);
      }
    };

    run();
    return () => { cancelled = true; };
  }, [viewMode, dateViewDateKey, courses]);

  // Liste « élève existant » sans doublons : une entrée par personne
  // (fiches students/ et users/ regroupées, voir utils/studentIdentity.js)
  const studentOptions = useMemo(() => {
    const groups = groupPeople(allStudents.map(s => ({
      id: s.id, firstName: s.firstName, lastName: s.lastName, birth: recordBirth(s),
      links: [s.userId, s.studentId].filter(Boolean), mergedInto: s.mergedInto || null,
      separate: s.separateIdentity === true,
    })));
    const byId = new Map(allStudents.map(s => [s.id, s]));
    return groups.map(g => {
      const score = (r) => { const s = byId.get(r.id); return (s.mergedInto ? -100 : 0) + (s._src === 'users' ? 4 : 0) + (r.birth ? 1 : 0); };
      const best = [...g].sort((a, b) => score(b) - score(a))[0];
      const birth = g.map(r => r.birth).find(Boolean) || null;
      return { ...byId.get(best.id), birth };
    }).sort((a, b) => `${a.lastName || ''} ${a.firstName || ''}`.localeCompare(`${b.lastName || ''} ${b.firstName || ''}`, 'fr'));
  }, [allStudents]);

  // Préremplir email/téléphone quand un élève existant est choisi dans le dialog d'ajout
  useEffect(() => {
    if (!selectedStudentId) return;
    const s = allStudents.find(st => st.id === selectedStudentId);
    if (s) {
      setNewStudent(prev => ({
        ...prev,
        email: s.contactInfo?.email || s.email || '',
        phone: s.contactInfo?.phone || s.phone || '',
      }));
    }
  }, [selectedStudentId, allStudents]);

  // ── Gestion présence ───────────────────────────────────────────────────────

  // Toggle Présent/Absent : newValue vaut true, false, ou null si l'admin
  // reclique le bouton déjà sélectionné (ToggleButtonGroup exclusive) —
  // dans ce cas on retire le choix (redevient "non renseigné").
  const handlePresenceToggle = (attendeeId, newValue) => {
    if (!selectedDate) return;
    setAttendance(prev => {
      const dateState = { ...(prev[selectedDate] || {}) };
      if (newValue === null) {
        delete dateState[attendeeId];
      } else {
        dateState[attendeeId] = newValue;
      }
      return { ...prev, [selectedDate]: dateState };
    });
  };

  const saveAttendance = async () => {
    if (!selectedCourseId || !selectedDate) return;
    setSaving(true);
    setSaveSuccess(false);
    try {
      const formattedDate  = toDateKey(selectedDate);
      const dateAttendance = attendance[selectedDate] || {};
      const warnings       = [];
      let cardsChanged     = false;

      for (const attendeeId of Object.keys(dateAttendance)) {
        const isPresent = dateAttendance[attendeeId];
        const att       = attendees.find(a => a.id === attendeeId);
        if (!att) continue;

        // ── Carte 10 : décompte / recrédit automatique ──────────────────────
        const reg = att.registrationId ? regsByIdRef.current[att.registrationId] : null;
        if (reg && registrationKind(reg) === 'card') {
          const name = `${att.firstName} ${att.lastName}`.trim();
          const consumedHere = cardCredits(reg).consumed.some(cl => consumptionMatches(cl, formattedDate, selectedCourseId));
          try {
            if (isPresent === true && !consumedHere) {
              await consumeCardCredit(reg.id, {
                dateKey: formattedDate,
                courseId: selectedCourseId,
                courseTitle: selectedCourse?.title || null,
                source: 'admin_attendance',
              });
              att.consumedFromCard = true;
              cardsChanged = true;
            } else if (isPresent === false && consumedHere) {
              const credited = await removeCardConsumption(reg.id, {
                dateKey: formattedDate, courseId: selectedCourseId, onlySources: ADMIN_CARD_SOURCES,
              });
              if (credited) { att.consumedFromCard = false; cardsChanged = true; }
              else warnings.push(`${name} : absent, mais le cours avait été décompté via l'app (non recrédité).`);
            }
          } catch (e) {
            if (e?.message === 'CARD_EXHAUSTED') warnings.push(`${name} : carte 10 épuisée — présence enregistrée sans décompte.`);
            else { console.error('Erreur décompte carte 10:', e); warnings.push(`${name} : décompte carte 10 impossible.`); }
          }
        }

        const recordKey = att.recordKey || att.studentId || attendeeId;
        // Email du contact : site (contactInfo.email) ou app/parent (parentInfo.parentEmail)
        const contactEmail = att.contactInfo?.email || att.parentInfo?.parentEmail || null;
        await setDoc(
          doc(db, `courses/${selectedCourseId}/attendance/${formattedDate}/records`, recordKey),
          {
            present:          isPresent,
            status:           isPresent ? 'present' : 'absent',
            studentId:        att.studentId || '',
            registrationId:   att.registrationId || '',
            firstName:        att.firstName || '',
            lastName:         att.lastName  || '',
            updatedFrom:      'admin_panel',
            source:           att.source || 'admin_panel',
            consumedFromCard: att.consumedFromCard || false,
            // Ajoutés pour le mail automatique de suivi d'essai (cf. sendTrialAttendanceFollowup)
            plan:             att.plan || '',
            planName:         att.planName || '',
            email:            contactEmail,
            timestamp:        Timestamp.now(),
          },
          { merge: true }
        );
      }

      setSaveWarnings(warnings);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
      // Crédits modifiés : on recharge pour mettre à jour les badges carte
      if (cardsChanged) await fetchAttendees();
    } catch (e) {
      console.error('Erreur sauvegarde:', e);
    } finally {
      setSaving(false);
    }
  };

  // ── Titulaire de carte 10 venu sans réserver : présent + décompte ─────────
  const markCardHolderPresent = async (holder) => {
    if (!selectedCourseId || !selectedDate) return;
    const formattedDate = toDateKey(selectedDate);
    setCardActionId(holder.id);
    try {
      await consumeCardCredit(holder.reg.id, {
        dateKey: formattedDate,
        courseId: selectedCourseId,
        courseTitle: selectedCourse?.title || null,
        source: 'admin_attendance',
      });
      const recordKey = recordKeyFor(holder.reg, holder.person, holder.idx);
      await setDoc(
        doc(db, `courses/${selectedCourseId}/attendance/${formattedDate}/records`, recordKey),
        {
          present:          true,
          status:           'present',
          studentId:        recordKey === `${holder.reg.id}_${holder.idx}` ? '' : recordKey,
          registrationId:   holder.reg.id,
          firstName:        holder.person.firstName || '',
          lastName:         holder.person.lastName  || '',
          updatedFrom:      'admin_panel',
          source:           holder.reg.source || 'admin_panel',
          consumedFromCard: true,
          plan:             holder.reg.plan || 'carte10',
          planName:         holder.reg.planName || 'Carte 10 cours',
          email:            holder.reg.contactInfo?.email || holder.reg.parentInfo?.parentEmail || null,
          timestamp:        Timestamp.now(),
        },
        { merge: true }
      );
      await fetchAttendees();
    } catch (e) {
      console.error('Erreur carte 10:', e);
      setSaveWarnings([e?.message === 'CARD_EXHAUSTED'
        ? 'Cette carte 10 est épuisée.'
        : 'Impossible de décompter la carte 10.']);
    } finally {
      setCardActionId(null);
    }
  };

  // ── Ajouter manuellement ───────────────────────────────────────────────────

  const addStudentManually = async () => {
    if (!selectedStudentId && (!newStudent.firstName || !newStudent.lastName)) {
      alert('Veuillez sélectionner un élève ou renseigner un nom et prénom.'); return;
    }
    try {
      let studentData = {};
      let studentId   = '';

      // contactInfo n'est créé que si au moins un des deux champs est rempli,
      // pour ne pas polluer les records avec un objet {email:null, phone:null}
      const contactInfo = (newStudent.email || newStudent.phone)
        ? { email: newStudent.email || null, phone: newStudent.phone || null }
        : null;

      // « Nouvel élève » : s'il existe déjà une fiche pour cette personne
      // (même nom, date de naissance compatible), on la réutilise au lieu
      // d'en créer une nouvelle — c'était la principale source de doublons.
      const existing = !selectedStudentId
        ? findSamePerson(allStudents, { firstName: newStudent.firstName, lastName: newStudent.lastName, dateOfBirth: newStudent.birthDate })
        : null;
      const pickedId = selectedStudentId || existing?.id || '';

      if (pickedId) {
        let s = allStudents.find(st => st.id === pickedId);
        if (!s) {
          const snap = await getDoc(doc(db, 'students', pickedId));
          s = snap.exists() ? { id: pickedId, ...snap.data() } : null;
        }
        if (s) {
          const birth = s.birthDate || s.dateOfBirth || newStudent.birthDate || null;
          studentData = { firstName: s.firstName, lastName: s.lastName, birthDate: birth, studentId: pickedId };
          studentId   = pickedId;
          // Élève venu de users/ (compte app) sans fiche students : on la crée
          // au MÊME id. Et si on a rempli l'email ou le téléphone, on les
          // sauvegarde pour ne plus avoir à les ressaisir.
          if (s._src === 'users' || contactInfo) {
            await setDoc(doc(db, 'students', pickedId), {
              ...(s._src === 'users' ? { firstName: s.firstName || '', lastName: s.lastName || '', userId: pickedId } : {}),
              ...(contactInfo ? { contactInfo } : {}),
            }, { merge: true });
          }
        }
      }
      if (!studentId) {
        const sRef  = await addDoc(collection(db, 'students'), {
          firstName: newStudent.firstName, lastName: newStudent.lastName,
          birthDate: newStudent.birthDate || null,
          contactInfo,
          createdAt: Timestamp.now(),
        });
        studentData = { firstName: newStudent.firstName, lastName: newStudent.lastName, birthDate: newStudent.birthDate, studentId: sRef.id };
        studentId   = sRef.id;
        // Visible tout de suite dans la liste (pas de second doublon dans la même session)
        setAllStudents(prev => [...prev, {
          id: sRef.id, firstName: newStudent.firstName, lastName: newStudent.lastName,
          birthDate: newStudent.birthDate || null, _src: 'students',
        }]);
      }

      const isTrial = newStudent.planType === 'essai';
      const manRef = await addDoc(
        collection(db, `courses/${selectedCourseId}/manual_attendance`),
        {
          ...studentData, contactInfo,
          plan:     isTrial ? 'essai' : 'manual',
          planName: isTrial ? "Cours d'essai" : 'Ajout manuel',
          date:     newStudent.addToAllDates ? null : toDateKey(selectedDate),
          allDates: newStudent.addToAllDates, source: 'manual', addedAt: Timestamp.now(),
        }
      );

      const newAtt = {
        id: `manual_${manRef.id}`, ...studentData,
        plan:     isTrial ? 'essai' : 'manual',
        planName: isTrial ? "Cours d'essai" : 'Ajout manuel',
        manuallyAdded: true,
        addedForAllDates: newStudent.addToAllDates, manualAttendanceId: manRef.id,
        source: 'manual', consumedFromCard: false, contactInfo,
      };
      setAttendees(prev => [...prev, newAtt]);
      setAttendance(prev => ({ ...prev, [selectedDate]: { ...(prev[selectedDate] || {}), [newAtt.id]: true } }));
      setSelectedStudentId('');
      setNewStudent({ firstName: '', lastName: '', birthDate: '', email: '', phone: '', planType: 'manual', addToAllDates: false });
      setAddStudentDialogOpen(false);
    } catch (e) {
      console.error('Erreur ajout élève:', e);
    }
  };

  // ── Supprimer ──────────────────────────────────────────────────────────────

  const deleteStudentFromCourse = async () => {
    if (!studentToDelete) return;
    try {
      if (studentToDelete.manuallyAdded && studentToDelete.manualAttendanceId) {
        await deleteDoc(doc(db, `courses/${selectedCourseId}/manual_attendance`, studentToDelete.manualAttendanceId));
      }
      setAttendees(prev => prev.filter(a => a.id !== studentToDelete.id));
      if (selectedDate && attendance[selectedDate]) {
        const upd = { ...attendance[selectedDate] };
        delete upd[studentToDelete.id];
        setAttendance(prev => ({ ...prev, [selectedDate]: upd }));
      }
      setDeleteDialogOpen(false);
      setStudentToDelete(null);
    } catch (e) {
      console.error('Erreur suppression:', e);
    }
  };

  // ── Compteurs ──────────────────────────────────────────────────────────────

  const dateAttendance = attendance[selectedDate] || {};
  const presentCount   = Object.values(dateAttendance).filter(v => v === true).length;
  const absentCount    = Object.values(dateAttendance).filter(v => v === false).length;
  const totalCount     = attendees.length;

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ p: 0 }}>
      {/* ── AppBar ──────────────────────────────────────────────────────────── */}
      <AppBar position="static" elevation={0} sx={{ backgroundColor: '#7C3AED', mb: 3 }}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={() => navigate('/')} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <EventIcon sx={{ mr: 1 }} />
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 'bold' }}>
            Gestion des présences
          </Typography>
        </Toolbar>
      </AppBar>

      <Box sx={{ p: 3 }}>
        {/* ── Vue : par cours (habituelle) ou par date (tous cours d'un jour) ─── */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <ToggleButtonGroup
            exclusive
            value={viewMode}
            onChange={(e, val) => { if (val) setViewMode(val); }}
            size="small"
          >
            <ToggleButton value="course" sx={{ px: 3 }}>Par cours</ToggleButton>
            <ToggleButton value="date"   sx={{ px: 3 }}>Par date</ToggleButton>
          </ToggleButtonGroup>
        </Paper>

        {/* ── Vue "par date" : toutes les séances du jour, tous cours ────────── */}
        {viewMode === 'date' && (
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
              <Tooltip title="Jour précédent">
                <IconButton
                  onClick={goDateViewPrevDay}
                  sx={{ border: '1px solid #e0e0e0', borderRadius: 2, '&:hover': { borderColor: '#7C3AED', color: '#7C3AED' } }}
                >
                  <PrevIcon />
                </IconButton>
              </Tooltip>

              <Typography variant="h6" sx={{ fontWeight: 'bold', flexGrow: 1, textAlign: 'center' }}>
                {moment(dateViewDateKey).format('dddd D MMMM YYYY')}
              </Typography>

              <Tooltip title="Jour suivant">
                <IconButton
                  onClick={goDateViewNextDay}
                  sx={{ border: '1px solid #e0e0e0', borderRadius: 2, '&:hover': { borderColor: '#7C3AED', color: '#7C3AED' } }}
                >
                  <NextIcon />
                </IconButton>
              </Tooltip>

              <TextField
                type="date"
                size="small"
                value={dateViewDateKey}
                onChange={(e) => e.target.value && setDateViewDateKey(e.target.value)}
                sx={{ ml: 1 }}
              />
              <Button size="small" onClick={() => setDateViewDateKey(moment().format('YYYY-MM-DD'))}>
                Aujourd'hui
              </Button>
            </Box>

            {dateViewLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress size={24} />
              </Box>
            ) : dateViewCourses.length === 0 ? (
              <Typography color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                Aucun cours prévu ce jour-là.
              </Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Cours</TableCell>
                      <TableCell>Horaire</TableCell>
                      <TableCell align="center">Attendu(s)</TableCell>
                      <TableCell align="center">Présent(s)</TableCell>
                      <TableCell align="center">Absent(s)</TableCell>
                      <TableCell align="center">Statut</TableCell>
                      <TableCell align="right">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dateViewCourses.map((c) => {
                      const saisie = c.present + c.absent;
                      const done   = c.expectedCount > 0 && saisie >= c.expectedCount;
                      return (
                        <TableRow
                          key={c.courseId}
                          hover
                          sx={{ cursor: 'pointer' }}
                          onClick={() => openCourseFromDateView(c.courseId, dateViewDateKey)}
                        >
                          <TableCell sx={{ fontWeight: 600 }}>{c.title}</TableCell>
                          <TableCell>
                            {c.startTime ? `${c.startTime}${c.endTime ? ` - ${c.endTime}` : ''}` : '—'}
                          </TableCell>
                          <TableCell align="center">{c.expectedCount}</TableCell>
                          <TableCell align="center">{c.present}</TableCell>
                          <TableCell align="center">{c.absent}</TableCell>
                          <TableCell align="center">
                            {saisie === 0 ? (
                              <Chip label="Pas encore saisi" size="small" color="warning" variant="outlined" />
                            ) : done ? (
                              <Chip label="Complet" size="small" color="success" />
                            ) : (
                              <Chip label="Partiel" size="small" color="warning" />
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={(e) => { e.stopPropagation(); openCourseFromDateView(c.courseId, dateViewDateKey); }}
                              sx={{ borderColor: '#7C3AED', color: '#7C3AED' }}
                            >
                              Voir les présences
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        )}

        {/* ── Vue "par cours" (habituelle) ────────────────────────────────────── */}
        {viewMode === 'course' && (
          <>
        {/* ── Sélection du cours ──────────────────────────────────────────────── */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
            Sélectionner un cours
          </Typography>
          {loading ? (
            <CircularProgress size={24} />
          ) : (
            <FormControl fullWidth>
              <InputLabel>Cours</InputLabel>
              <Select
                value={selectedCourseId}
                label="Cours"
                onChange={(e) => setSelectedCourseId(e.target.value)}
              >
                {courses.map(c => (
                  <MenuItem key={c.id} value={c.id}>{c.title}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Paper>

        {/* ── Sélection de la date ─────────────────────────────────────────── */}
        {selectedCourseId && (
          <>
            <Paper sx={{ p: 3, mb: 3 }}>
              {/* Titre + navigation flèches + bouton calendrier */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                {/* Flèche précédente */}
                <Tooltip title="Séance précédente">
                  <span>
                    <IconButton
                      onClick={goPrev}
                      disabled={selectedDateIndex <= 0}
                      sx={{
                        border: '1px solid #e0e0e0',
                        borderRadius: 2,
                        '&:not(:disabled)': { borderColor: '#7C3AED', color: '#7C3AED' },
                      }}
                    >
                      <PrevIcon />
                    </IconButton>
                  </span>
                </Tooltip>

                {/* Titre date */}
                <Typography variant="h6" sx={{ fontWeight: 'bold', flexGrow: 1, textAlign: 'center' }}>
                  {selectedDate
                    ? `Séance du ${moment(selectedDate).format('dddd D MMMM YYYY')}`
                    : 'Sélectionner une date'}
                </Typography>

                {/* Flèche suivante */}
                <Tooltip title="Séance suivante">
                  <span>
                    <IconButton
                      onClick={goNext}
                      disabled={selectedDateIndex >= sortedDates.length - 1}
                      sx={{
                        border: '1px solid #e0e0e0',
                        borderRadius: 2,
                        '&:not(:disabled)': { borderColor: '#7C3AED', color: '#7C3AED' },
                      }}
                    >
                      <NextIcon />
                    </IconButton>
                  </span>
                </Tooltip>

                {/* Bouton calendrier */}
                <Button
                  variant="outlined"
                  startIcon={<CalendarToday />}
                  onClick={() => setDateDialogOpen(true)}
                  sx={{ borderColor: '#7C3AED', color: '#7C3AED', ml: 1 }}
                >
                  Changer de date
                </Button>
              </Box>

              {/* Indicateur position dans les séances */}
              {sortedDates.length > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, textAlign: 'center' }}>
                  Séance {selectedDateIndex + 1} / {sortedDates.length}
                </Typography>
              )}

              {/* Chips résumé */}
              {selectedDate && (
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Chip label={`${totalCount} participant(s) attendu(s)`} variant="outlined" />
                  <Chip label={`${presentCount} présent(s)`}              color="success" />
                  <Chip label={`${absentCount} absent(s)`}                color="error" />
                  <Chip label={`${totalCount - presentCount - absentCount} non renseigné(s)`} color="warning" />
                </Box>
              )}
            </Paper>

            {/* ── Tableau participants ──────────────────────────────────────── */}
            {selectedDate && (
              <Paper sx={{ width: '100%', overflow: 'hidden', mb: 3 }}>
                <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    Participants — {moment(selectedDate).format('DD/MM/YYYY')}
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<PersonAddIcon />}
                    onClick={() => setAddStudentDialogOpen(true)}
                    sx={{ borderColor: '#7C3AED', color: '#7C3AED' }}
                  >
                    Ajouter
                  </Button>
                </Box>
                <Divider />

                {dateLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <>
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                            <TableCell sx={{ fontWeight: 'bold' }}>Participant</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Formule</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Source</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Contact</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 'bold' }}>Présence</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {attendees.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={6} align="center" sx={{ py: 4, color: '#999' }}>
                                Aucun participant pour cette date.
                              </TableCell>
                            </TableRow>
                          ) : (
                            attendees.map(att => (
                              <TableRow key={att.id} hover>
                                {/* Nom */}
                                <TableCell>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                    <Typography fontWeight="bold">
                                      {att.firstName} {att.lastName}
                                    </Typography>
                                    <ImageConsentIcon consent={att.imageConsent} />
                                  </Box>
                                  {att.birthDate && (
                                    <Typography variant="caption" color="text.secondary">
                                      {moment(att.birthDate).format('DD/MM/YYYY')}
                                    </Typography>
                                  )}
                                </TableCell>

                                {/* Formule */}
                                <TableCell>
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    <Chip
                                      label={att.planName || att.plan || 'N/A'}
                                      color={planColor(att.plan)}
                                      size="small"
                                    />
                                    {/* Situation de paiement (retard, carte épuisée…) */}
                                    {att.billingBadge && (
                                      <Tooltip title={att.billingBadge.tooltip || ''}>
                                        <Chip
                                          label={att.billingBadge.label}
                                          size="small"
                                          onClick={att.registrationId
                                            ? () => navigate('/subscriptions', { state: { openRegistrationId: att.registrationId } })
                                            : undefined}
                                          sx={{
                                            backgroundColor: BADGE_TONES[att.billingBadge.tone]?.bg,
                                            color: BADGE_TONES[att.billingBadge.tone]?.color,
                                            fontSize: '0.7rem',
                                            height: 20,
                                            fontWeight: 600,
                                          }}
                                        />
                                      </Tooltip>
                                    )}
                                    {att.otherPlans?.length > 0 && (
                                      <Typography variant="caption" color="text.secondary">
                                        aussi : {att.otherPlans.join(', ')}
                                      </Typography>
                                    )}
                                    {/* Badge spécial si consommé via carte10 */}
                                    {att.consumedFromCard && (
                                      <Chip
                                        icon={<CardIcon fontSize="small" />}
                                        label="Via carte 10"
                                        size="small"
                                        sx={{
                                          backgroundColor: '#fff3e0',
                                          color: '#e65100',
                                          fontSize: '0.7rem',
                                          height: 20,
                                        }}
                                      />
                                    )}
                                  </Box>
                                </TableCell>

                                {/* Source */}
                                <TableCell>
                                  <Chip
                                    label={sourceLabel(att.source)}
                                    size="small"
                                    icon={sourceIcon(att.source) ?? undefined}
                                    variant="outlined"
                                    sx={APP_SOURCES.has(att.source) ? { borderColor: '#7C3AED', color: '#7C3AED' } : {}}
                                  />
                                </TableCell>

                                {/* Contact */}
                                <TableCell>
                                  <Typography variant="caption">
                                    {att.contactInfo?.email
                                      || att.parentInfo?.parentEmail
                                      || (APP_SOURCES.has(att.source) ? '(via app)' : '—')}
                                  </Typography>
                                </TableCell>

                                {/* Présence */}
                                <TableCell align="center">
                                  <ToggleButtonGroup
                                    size="small"
                                    exclusive
                                    value={
                                      dateAttendance[att.id] === true  ? 'present' :
                                      dateAttendance[att.id] === false ? 'absent'  : null
                                    }
                                    onChange={(e, newValue) => {
                                      const mapped = newValue === 'present' ? true
                                        : newValue === 'absent' ? false
                                        : null;
                                      handlePresenceToggle(att.id, mapped);
                                    }}
                                  >
                                    <ToggleButton
                                      value="present"
                                      sx={{
                                        px: 1.5,
                                        '&.Mui-selected': { backgroundColor: '#e8f5e9', color: '#2e7d32', '&:hover': { backgroundColor: '#d0ecd1' } },
                                      }}
                                    >
                                      <Tooltip title="Présent">
                                        <CheckIcon fontSize="small" />
                                      </Tooltip>
                                    </ToggleButton>
                                    <ToggleButton
                                      value="absent"
                                      sx={{
                                        px: 1.5,
                                        '&.Mui-selected': { backgroundColor: '#ffebee', color: '#c62828', '&:hover': { backgroundColor: '#fdd8db' } },
                                      }}
                                    >
                                      <Tooltip title="Absent">
                                        <CloseIcon fontSize="small" />
                                      </Tooltip>
                                    </ToggleButton>
                                  </ToggleButtonGroup>
                                </TableCell>

                                {/* Actions */}
                                <TableCell align="center">
                                  {att.manuallyAdded && (
                                    <Tooltip title="Supprimer">
                                      <IconButton size="small" color="error"
                                        onClick={() => { setStudentToDelete(att); setDeleteDialogOpen(true); }}>
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>

                    {saveSuccess && (
                      <Box sx={{ p: 2, backgroundColor: '#e8f5e9', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CheckIcon sx={{ color: '#2e7d32' }} />
                        <Typography color="success.main">Présences enregistrées !</Typography>
                      </Box>
                    )}
                    {saveWarnings.length > 0 && (
                      <Box sx={{ p: 2, backgroundColor: '#fff3e0' }}>
                        {saveWarnings.map((w, i) => (
                          <Typography key={i} variant="body2" sx={{ color: '#e65100' }}>⚠️ {w}</Typography>
                        ))}
                        <Button size="small" onClick={() => setSaveWarnings([])} sx={{ mt: 0.5 }}>OK</Button>
                      </Box>
                    )}

                    <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end' }}>
                      <Button
                        variant="contained"
                        onClick={saveAttendance}
                        disabled={saving}
                        startIcon={saving ? <CircularProgress size={20} /> : <CheckIcon />}
                        sx={{ backgroundColor: '#7C3AED', '&:hover': { backgroundColor: '#6D28D9' }, minWidth: 220, py: 1.5 }}
                      >
                        {saving ? 'Enregistrement...' : 'Enregistrer les présences'}
                      </Button>
                    </Box>
                  </>
                )}
              </Paper>
            )}

            {/* ── Cartes 10 valables sur ce cours, sans réservation ce jour ───── */}
            {selectedDate && !dateLoading && cardHolders.length > 0 && (
              <Paper sx={{ width: '100%', overflow: 'hidden', mb: 3 }}>
                <Box sx={{ p: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CardIcon sx={{ color: '#e65100' }} /> Cartes 10 cours sans réservation pour cette séance
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Élève venu sans réserver dans l'app : un clic le marque présent et décompte 1 crédit de sa carte.
                  </Typography>
                </Box>
                <Divider />
                <Table size="small">
                  <TableBody>
                    {cardHolders.map(h => (
                      <TableRow key={h.id} hover>
                        <TableCell>
                          <Typography fontWeight="bold">{h.person.firstName} {h.person.lastName}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={`${h.credits.remaining} crédit(s) restant(s)`}
                            color={h.credits.remaining <= 2 ? 'warning' : 'default'}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={cardActionId === h.id ? <CircularProgress size={14} /> : <CheckIcon />}
                            disabled={!!cardActionId}
                            onClick={() => markCardHolderPresent(h)}
                            sx={{ borderColor: '#7C3AED', color: '#7C3AED' }}
                          >
                            Présent · −1 crédit
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            )}
          </>
        )}
          </>
        )}

        {/* ── Dialog : ajouter un élève ───────────────────────────────────────── */}
        <Dialog open={addStudentDialogOpen} onClose={() => setAddStudentDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ backgroundColor: '#7C3AED', color: 'white' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <PersonAddIcon sx={{ mr: 1 }} /> Ajouter un élève
            </Box>
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Élève existant</InputLabel>
              <Select value={selectedStudentId} label="Élève existant"
                onChange={(e) => setSelectedStudentId(e.target.value)}>
                <MenuItem value="">-- Nouvel élève --</MenuItem>
                {studentOptions.map(s => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.firstName} {s.lastName}
                    {s.birth && ` (${moment(s.birth).format('DD/MM/YYYY')})`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {!selectedStudentId && (
              <>
                <TextField autoFocus margin="dense" label="Prénom *" fullWidth variant="outlined"
                  value={newStudent.firstName} onChange={(e) => setNewStudent({ ...newStudent, firstName: e.target.value })} sx={{ mb: 2 }} />
                <TextField margin="dense" label="Nom *" fullWidth variant="outlined"
                  value={newStudent.lastName} onChange={(e) => setNewStudent({ ...newStudent, lastName: e.target.value })} sx={{ mb: 2 }} />
                <TextField margin="dense" label="Date de naissance" type="date" fullWidth variant="outlined"
                  InputLabelProps={{ shrink: true }}
                  value={newStudent.birthDate} onChange={(e) => setNewStudent({ ...newStudent, birthDate: e.target.value })} sx={{ mb: 2 }} />
              </>
            )}
            {/* Email / téléphone : utile pour les élèves essai ajoutés à la main
                (permet au mail de suivi automatique de leur être envoyé) */}
            <TextField margin="dense" label="Email" type="email" fullWidth variant="outlined"
              value={newStudent.email} onChange={(e) => setNewStudent({ ...newStudent, email: e.target.value })}
              helperText="Requis pour recevoir le mail de suivi si formule essai" sx={{ mb: 2 }} />
            <TextField margin="dense" label="Téléphone" fullWidth variant="outlined"
              value={newStudent.phone} onChange={(e) => setNewStudent({ ...newStudent, phone: e.target.value })} />
            {/* Formule : détermine si cet ajout déclenche le mail de suivi essai */}
            <Typography variant="body2" sx={{ mb: 1, color: '#374151', fontWeight: 600 }}>Type d'ajout</Typography>
            <ToggleButtonGroup
              exclusive
              fullWidth
              value={newStudent.planType}
              onChange={(e, val) => { if (val) setNewStudent({ ...newStudent, planType: val }); }}
              sx={{ mb: 2 }}
            >
              <ToggleButton value="manual">Ajout standard</ToggleButton>
              <ToggleButton value="essai">Cours d'essai</ToggleButton>
            </ToggleButtonGroup>
            {newStudent.planType === 'essai' && !newStudent.email && (
              <Typography variant="caption" sx={{ display: 'block', mb: 2, color: '#c62828' }}>
                ⚠️ Sans email, le mail de suivi essai ne pourra pas être envoyé.
              </Typography>
            )}
            <FormControlLabel
              control={<Switch checked={newStudent.addToAllDates}
                onChange={(e) => setNewStudent({ ...newStudent, addToAllDates: e.target.checked })} />}
              label="Ajouter pour toutes les dates de ce cours"
              sx={{ mt: 3, display: 'block' }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAddStudentDialogOpen(false)}>Annuler</Button>
            <Button onClick={addStudentManually} variant="contained"
              disabled={!selectedStudentId && (!newStudent.firstName || !newStudent.lastName)}
              sx={{ backgroundColor: '#7C3AED', '&:hover': { backgroundColor: '#6D28D9' } }}>
              Ajouter
            </Button>
          </DialogActions>
        </Dialog>

        {/* ── Dialog : sélectionner une date ─────────────────────────────────── */}
        <Dialog open={dateDialogOpen} onClose={() => setDateDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ backgroundColor: '#7C3AED', color: 'white' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <CalendarToday sx={{ mr: 1 }} /> Sélectionner une date
            </Box>
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
              {sortedDates.map((iso, i) => {
                const m = moment(iso);
                return (
                  <Card key={i} sx={{
                    mb: 1, cursor: 'pointer',
                    border: selectedDate === iso ? '2px solid #7C3AED' : '1px solid #e0e0e0',
                    backgroundColor: selectedDate === iso ? '#f3e8fd' : 'white',
                  }} onClick={() => handleDateSelect(iso)}>
                    <CardContent sx={{ py: 1.5 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                          <Typography fontWeight={selectedDate === iso ? 'bold' : 'normal'}>
                            {m.format('dddd D MMMM YYYY')}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            à {m.format('HH:mm')} — séance {i + 1}/{sortedDates.length}
                          </Typography>
                        </Box>
                        {m.isSame(moment(), 'day') && <Chip label="Aujourd'hui" size="small" color="primary" />}
                      </Box>
                    </CardContent>
                  </Card>
                );
              })}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDateDialogOpen(false)}>Fermer</Button>
          </DialogActions>
        </Dialog>

        {/* ── Dialog : supprimer ─────────────────────────────────────────────── */}
        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ backgroundColor: '#f44336', color: 'white' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <DeleteIcon sx={{ mr: 1 }} /> Supprimer un élève
            </Box>
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            <Typography>Êtes-vous sûr de vouloir supprimer cet élève du cours ?</Typography>
            {studentToDelete && (
              <Typography fontWeight="bold" sx={{ mt: 2 }}>
                {studentToDelete.firstName} {studentToDelete.lastName}
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Cette action est irréversible.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Annuler</Button>
            <Button onClick={deleteStudentFromCourse} variant="contained" color="error" startIcon={<DeleteIcon />}>
              Supprimer
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
}

export default AttendancePage;